import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI, uploadAPI, certificatesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Save, Upload, X, Plus, Waves, Camera, PenTool, ShieldCheck, ShieldX, Award } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { isAdmin } = useAuth();
  const logoInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const [settings, setSettings] = useState({
    logo_url: '',
    signature_url: '',
    academy_name: '',
    about_text: '',
    gallery_images: [],
    contact_email: '',
    contact_phone: '',
    address: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  
  // Certificate management
  const [pendingCertificates, setPendingCertificates] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [approvingCertId, setApprovingCertId] = useState(null);

  useEffect(() => {
    loadSettings();
    loadPendingCertificates();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingCertificates = async () => {
    setLoadingCerts(true);
    try {
      const response = await certificatesAPI.getAll({ approved: false });
      setPendingCertificates(response.data);
    } catch (error) {
      console.error('Failed to load certificates');
    } finally {
      setLoadingCerts(false);
    }
  };

  const handleApproveCertificate = async (certId) => {
    setApprovingCertId(certId);
    try {
      await certificatesAPI.approve(certId);
      toast.success('Certificate approved!');
      loadPendingCertificates();
    } catch (error) {
      toast.error('Failed to approve certificate');
    } finally {
      setApprovingCertId(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addGalleryImage = () => {
    if (newImageUrl && newImageUrl.trim()) {
      setSettings({
        ...settings,
        gallery_images: [...(settings.gallery_images || []), newImageUrl.trim()]
      });
      setNewImageUrl('');
    }
  };

  const removeGalleryImage = (index) => {
    const newImages = [...(settings.gallery_images || [])];
    newImages.splice(index, 1);
    setSettings({ ...settings, gallery_images: newImages });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const response = await uploadAPI.uploadPhoto(file, 'logo', settings.id || 'academy');
      setSettings({ ...settings, logo_url: response.data.photo_url });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file');
      return;
    }
    
    setUploadingSignature(true);
    try {
      // Upload to landing/upload endpoint with image_type="signature"
      // This updates landing_config.certificate.signature_url in MongoDB
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'signature');
      
      const response = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const data = await response.json();
      // Backend returns { image_url: "...", filename: "...", type: "signature" }
      setSettings({ ...settings, signature_url: data.image_url });
      toast.success('Signature uploaded and saved to certificate configuration');
    } catch (error) {
      toast.error('Failed to upload signature');
    } finally {
      setUploadingSignature(false);
    }
  };

  const getLogoUrl = () => {
    if (!settings.logo_url) return null;
    if (settings.logo_url.startsWith('http')) return settings.logo_url;
    return `${BACKEND_URL}${settings.logo_url}`;
  };

  const getSignatureUrl = () => {
    if (!settings.signature_url) return null;
    if (settings.signature_url.startsWith('http')) return settings.signature_url;
    return `${BACKEND_URL}${settings.signature_url}`;
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Only administrators can access settings</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500">Manage academy branding and information</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-settings-btn">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Branding */}
      <Card>
        <CardHeader>
          <CardTitle>Branding</CardTitle>
          <CardDescription>Academy name and logo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo Preview with Upload */}
          <div className="flex items-center gap-6">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="logo-file-input"
            />
            <div 
              className="w-24 h-24 rounded-2xl bg-primary/10 flex items-center justify-center overflow-hidden cursor-pointer relative group"
              onClick={() => logoInputRef.current?.click()}
            >
              {uploadingLogo ? (
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              ) : getLogoUrl() ? (
                <img 
                  src={getLogoUrl()} 
                  alt="Logo" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <Waves className="w-12 h-12 text-primary" />
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-2xl">
                <Camera className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <Label>Logo</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={settings.logo_url || ''}
                  onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png or upload"
                  data-testid="logo-url-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="upload-logo-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Click the logo to upload or enter a URL</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Academy Name</Label>
            <Input
              value={settings.academy_name || ''}
              onChange={(e) => setSettings({ ...settings, academy_name: e.target.value })}
              placeholder="Eswatini Seals Swim Academy"
              data-testid="academy-name-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Certificate Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            Certificate Settings
          </CardTitle>
          <CardDescription>Logo and signature for generated certificates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Signature Upload */}
          <div className="flex items-center gap-6">
            <input
              ref={signatureInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleSignatureUpload}
              className="hidden"
              data-testid="signature-file-input"
            />
            <div 
              className="w-32 h-16 rounded-lg bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden cursor-pointer relative group"
              onClick={() => signatureInputRef.current?.click()}
            >
              {uploadingSignature ? (
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              ) : getSignatureUrl() ? (
                <img 
                  src={getSignatureUrl()} 
                  alt="Signature" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <PenTool className="w-8 h-8 text-slate-400" />
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                <Camera className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <Label>Digital Signature</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={settings.signature_url || ''}
                  onChange={(e) => setSettings({ ...settings, signature_url: e.target.value })}
                  placeholder="Upload signature image"
                  data-testid="signature-url-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => signatureInputRef.current?.click()}
                  disabled={uploadingSignature}
                  data-testid="upload-signature-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">This signature will appear on all approved certificates</p>
            </div>
          </div>

          {/* Pending Certificate Approvals */}
          <div className="pt-4 border-t">
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center justify-between">
              <span>Pending Certificate Approvals</span>
              <Button variant="ghost" size="sm" onClick={loadPendingCertificates} disabled={loadingCerts}>
                {loadingCerts && <Loader2 className="w-4 h-4 animate-spin" />}
              </Button>
            </h4>
            
            {pendingCertificates.length === 0 ? (
              <div className="text-center py-6 text-slate-400 bg-slate-50 rounded-lg">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No pending certificates</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {pendingCertificates.map((cert) => (
                  <div 
                    key={cert.id}
                    className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                    data-testid={`pending-cert-${cert.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{cert.student_name}</p>
                      <p className="text-sm text-slate-500">{cert.program_name} • Completed: {cert.completion_date}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleApproveCertificate(cert.id)}
                      disabled={approvingCertId === cert.id}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`approve-cert-${cert.id}`}
                    >
                      {approvingCertId === cert.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ShieldCheck className="w-4 h-4 mr-1" />
                          Approve
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* About Section */}
      <Card>
        <CardHeader>
          <CardTitle>About Section</CardTitle>
          <CardDescription>Information displayed on the About page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>About Text</Label>
            <Textarea
              value={settings.about_text || ''}
              onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
              placeholder="Describe your academy..."
              rows={6}
              data-testid="about-text-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Gallery */}
      <Card>
        <CardHeader>
          <CardTitle>Gallery</CardTitle>
          <CardDescription>Images displayed on the About page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              placeholder="Enter image URL"
              className="flex-1"
              data-testid="gallery-url-input"
            />
            <Button onClick={addGalleryImage} variant="outline" data-testid="add-gallery-image-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>

          {settings.gallery_images && settings.gallery_images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {settings.gallery_images.map((url, index) => (
                <div key={index} className="relative aspect-video rounded-lg overflow-hidden group">
                  <img 
                    src={url} 
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => removeGalleryImage(index)}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-gallery-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>Contact details displayed on the About page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={settings.contact_email || ''}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                placeholder="info@example.com"
                data-testid="contact-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={settings.contact_phone || ''}
                onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                placeholder="+268 7600 0000"
                data-testid="contact-phone-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={settings.address || ''}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              placeholder="Full address"
              data-testid="address-input"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
